import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../components/ui/chart";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

// Helper function to format Indian numbers
const formatIndianNumber = (num) => {
  if (num === undefined || num === null) return '';
  num = Number(num);
  if (Math.abs(num) >= 1e7) return (num / 1e7).toFixed(2) + ' Cr';
  if (Math.abs(num) >= 1e5) return (num / 1e5).toFixed(2) + ' L';
  return num.toLocaleString('en-IN');
};

// Helper function to get week key from date
const getWeekKey = (dateStr) => {
  const date = new Date(dateStr);
  const startOfWeek = new Date(date);
  startOfWeek.setDate(date.getDate() - date.getDay()); // Sunday as start of week
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  
  return `${startOfWeek.toISOString().split('T')[0]}/${endOfWeek.toISOString().split('T')[0]}`;
};

function ColorSelect({ value, onChange, sources, sourceColors }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <div
        className="flex items-center gap-2 px-2 py-1 bg-black border border-gray-700 rounded cursor-pointer min-w-[200px]"
        onClick={() => setIsOpen(!isOpen)}
      >
        {value !== "All" && (
          <span
            className="inline-block w-3 h-3 rounded-full"
            style={{ backgroundColor: sourceColors[value] }}
          />
        )}
        <span>{value === "All" ? "All Sources" : value}</span>
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-black border border-gray-700 rounded shadow-lg max-h-[400px] overflow-y-auto">
          <div
            className="px-2 py-1 cursor-pointer hover:bg-gray-800"
            onClick={() => {
              onChange("All");
              setIsOpen(false);
            }}
          >
            All Sources
          </div>
          {sources.map(source => (
            <div
              key={source}
              className="px-2 py-1 cursor-pointer hover:bg-gray-800 flex items-center gap-2"
              onClick={() => {
                onChange(source);
                setIsOpen(false);
              }}
            >
              <span
                className="inline-block w-3 h-3 rounded-full"
                style={{ backgroundColor: sourceColors[source] }}
              />
              <span>{source}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Generate a color for a source
const generateColor = (index) => {
  const colors = [
    "#1f77b4", // Blue
    "#ff7f0e", // Orange
    "#2ca02c", // Green
    "#d62728", // Red
    "#9467bd", // Purple
    "#8c564b", // Brown
    "#e377c2", // Pink
    "#7f7f7f", // Gray
    "#bcbd22", // Yellow-green
    "#17becf", // Cyan
    "#aec7e8", // Light blue
    "#ffbb78", // Light orange
    "#98df8a", // Light green
  ];
  return colors[index % colors.length];
};

const chartConfig = {
  line: {
    strokeWidth: 2,
    dot: false,
    connectNulls: true,
  },
};

export function SourceWisePNLChart({ data = {}, weeklyData = {} }) {
  const [timeRange, setTimeRange] = useState("all");
  const [viewType, setViewType] = useState("daily");
  const [filteredData, setFilteredData] = useState([]);
  const [sources, setSources] = useState([]);
  const [sourceColors, setSourceColors] = useState({});
  const [selectedSource, setSelectedSource] = useState("All");

  // Process data and extract sources
  useEffect(() => {
    console.log("SourceWisePNLChart received data:", data);
    console.log("SourceWisePNLChart received weeklyData:", weeklyData);
    
    // Determine which data to use for extracting sources
    const currentData = viewType === "daily" ? data : weeklyData;
    
    if (!currentData || Object.keys(currentData).length === 0) {
      console.log("No data available or empty object");
      setFilteredData([]);
      setSources([]);
      setSourceColors({});
      return;
    }

    // Get all source names from the object keys
    const sourceNames = Object.keys(currentData);
    console.log("Found source names:", sourceNames);

    // Create color mapping for sources
    const colors = {};
    sourceNames.forEach((source, index) => {
      colors[source] = generateColor(index);
    });

    setSources(sourceNames);
    setSourceColors(colors);
  }, [data, weeklyData, viewType]);

  // Transform the data from source-keyed object to array of date-keyed objects
  const transformData = (rawData, viewType) => {
    console.log("Transforming data for viewType:", viewType);
    console.log("Raw data:", rawData);
    if (!rawData || Object.keys(rawData).length === 0) return [];

    if (viewType === "daily") {
      // Handle daily view - use cumulative_PNL
      const allDates = new Set();
      Object.values(rawData).forEach(sourceData => {
        if (Array.isArray(sourceData)) {
          sourceData.forEach(item => {
            if (item && item.date) {
              allDates.add(item.date);
            }
          });
        }
      });

      const result = Array.from(allDates).map(date => {
        const dataPoint = { date };
        Object.entries(rawData).forEach(([source, sourceData]) => {
          if (Array.isArray(sourceData)) {
            const dayData = sourceData.find(item => item && item.date === date);
            // Use cumulative_PNL for daily data
            dataPoint[source] = dayData ? dayData.cumulative_PNL : null;
          }
        });
        return dataPoint;
      }).sort((a, b) => new Date(a.date) - new Date(b.date));

      console.log("Daily transformed result:", result);
      return result;
    } else {
      // Handle weekly view - use cumulative_PNL from weekly data
      const allWeeks = new Set();
      Object.values(rawData).forEach(sourceData => {
        if (Array.isArray(sourceData)) {
          sourceData.forEach(item => {
            if (item && item.week) {
              allWeeks.add(item.week);
            }
          });
        }
      });

      const result = Array.from(allWeeks).map(week => {
        const dataPoint = { date: week };
        Object.entries(rawData).forEach(([source, sourceData]) => {
          if (Array.isArray(sourceData)) {
            const weekData = sourceData.find(item => item && item.week === week);
            // Use cumulative_PNL for weekly data
            dataPoint[source] = weekData ? weekData.cumulative_PNL : null;
          }
        });
        return dataPoint;
      }).sort((a, b) => {
        const [aStart] = a.date.split("/");
        const [bStart] = b.date.split("/");
        return new Date(aStart) - new Date(bStart);
      });

      console.log("Weekly transformed result:", result);
      return result;
    }
  };

  // Update filtered data when data or viewType changes
  useEffect(() => {
    // Use the appropriate data source based on viewType
    const currentDataSource = viewType === "daily" ? data : weeklyData;
    
    if (!currentDataSource || Object.keys(currentDataSource).length === 0) {
      setFilteredData([]);
      return;
    }
    
    const transformedData = transformData(currentDataSource, viewType);
    let filtered = [...transformedData];

    // Apply time range filter
    if (timeRange !== "all" && filtered.length > 0) {
      const lastDate = filtered[filtered.length - 1].date;
      if (lastDate) {
        let lastDateTime;
        if (viewType === "daily") {
          lastDateTime = new Date(lastDate);
        } else {
          const [, endDate] = lastDate.split("/");
          lastDateTime = new Date(endDate);
        }

        filtered = filtered.filter(d => {
          if (!d || !d.date) return false;
          let date;
          if (viewType === "daily") {
            date = new Date(d.date);
          } else {
            const [, endDate] = d.date.split("/");
            date = new Date(endDate);
          }
          let days = 0;
          if (timeRange === "7d") days = 7;
          else if (timeRange === "15d") days = 15;
          else if (timeRange === "30d") days = 30;
          
          const timeDiff = lastDateTime - date;
          return timeDiff <= days * 24 * 60 * 60 * 1000;
        });
      }
    }

    setFilteredData(filtered);
  }, [data, weeklyData, viewType, timeRange]);

  // Get sources to display based on selection
  const getDisplaySources = () => {
    if (selectedSource === "All") {
      return sources;
    }
    return [selectedSource];
  };

  // Custom tooltip content component
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div className="bg-black border border-gray-700 rounded p-3 shadow-lg">
        <p className="text-gray-300 mb-2">
          {label ? (() => {
            if (viewType === "daily") {
              const dateObj = new Date(label);
              return dateObj.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric"
              });
            } else {
              const [start, end] = label.split("/");
              const startDate = new Date(start);
              const endDate = new Date(end);
              return `${startDate.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })} - ${endDate.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}`;
            }
          })() : ""}
        </p>
        <div className="space-y-1">
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center gap-2">
              <span
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: sourceColors[entry.name] }}
              />
              <span className="text-gray-300">{entry.name}:</span>
              <span className="text-white font-medium">
                {formatIndianNumber(entry.value)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Check if any data is available
  const hasData = (data && Object.keys(data).length > 0) || (weeklyData && Object.keys(weeklyData).length > 0);

  // If no data is available, show a message
  if (!hasData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Source-wise Cumulative PNL</CardTitle>
          <CardDescription>Cumulative PNL trends for individual sources</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[450px] w-full flex items-center justify-center">
            <p className="text-muted-foreground">No data available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Source-wise Cumulative PNL</CardTitle>
            <CardDescription>
              {selectedSource === "All"
                ? `Showing cumulative PNL for all sources (${viewType === "daily" ? "Daily" : "Weekly"})`
                : `Showing cumulative PNL for ${selectedSource} (${viewType === "daily" ? "Daily" : "Weekly"})`}
            </CardDescription>
            {/* Source color legend */}
            {selectedSource === "All" && (
              <div className="flex flex-wrap gap-3 mt-2">
                {sources.map(source => (
                  <div key={source} className="flex items-center gap-2">
                    <span
                      className="inline-block w-3 h-3 rounded-full"
                      style={{ backgroundColor: sourceColors[source] }}
                    />
                    <span className="text-sm text-gray-400">{source}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-row items-center gap-4 ml-4">
            {/* Custom source selector with color indicators */}
            <ColorSelect
              value={selectedSource}
              onChange={setSelectedSource}
              sources={sources}
              sourceColors={sourceColors}
            />
            {/* Time range selector */}
            <select
              value={timeRange}
              onChange={e => setTimeRange(e.target.value)}
              className="bg-black text-white border border-gray-700 rounded px-2 py-1"
            >
              <option value="all">ALL</option>
              <option value="7d">7 days</option>
              <option value="15d">15 days</option>
              <option value="30d">30 days</option>
            </select>
            <span className="font-semibold text-white/80">View:</span>
            <select
              value={viewType}
              onChange={e => setViewType(e.target.value)}
              className="bg-black text-white border border-gray-700 rounded px-2 py-1"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {filteredData.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            No data available for this selection.
            {viewType === "weekly" && (!weeklyData || Object.keys(weeklyData).length === 0) && (
              <div className="mt-2 text-sm">
                Weekly data not provided. Please ensure weeklyData prop is passed to the component.
              </div>
            )}
          </div>
        ) : (
          <ChartContainer className="h-[450px] w-full" config={chartConfig}>
            <LineChart data={filteredData} margin={{ left: 60, right: 12 }}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={12}
                tickFormatter={(value) => {
                  if (!value) return "";
                  if (viewType === "daily") {
                    const dateObj = new Date(value);
                    return dateObj.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
                  } else {
                    const [start] = value.split("/");
                    const dateObj = new Date(start);
                    return dateObj.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
                  }
                }}
                interval={Math.ceil(filteredData.length / 8)}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={1}
                tickCount={11}
                domain={["dataMin-200000", "dataMax+100000"]}
                tickFormatter={formatIndianNumber}
              />
              <Tooltip content={<CustomTooltip />} />
              {getDisplaySources().map(source => (
                <Line
                  key={source}
                  type="monotone"
                  dataKey={source}
                  stroke={sourceColors[source]}
                  strokeWidth={2}
                  dot={false}
                  name={source}
                  connectNulls={true}
                />
              ))}
            </LineChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}